(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const productJsonCache = new Map();

  function loadProductJson(handle) {
    if (!handle) return Promise.reject(new Error("Missing product handle"));
    if (!productJsonCache.has(handle)) {
      productJsonCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: {
            Accept: "application/json",
          },
        }).then((response) => {
          if (!response.ok) throw new Error("Unable to load product");
          return response.json();
        }),
      );
    }

    return productJsonCache.get(handle);
  }

  function formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return "";
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(amount);
    }

    const currency = window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency,
    }).format(amount / 100);
  }

  function escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
  }

  function updateInventoryText(item, product, variantId) {
    const count = item?.querySelector("[data-nept-inventory-count]");
    if (!item || !count) return;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((entry) => String(entry.id) === String(variantId)) || variants[0];
    const quantity = Number(variant?.inventory_quantity);
    if (Number.isFinite(quantity)) count.textContent = String(Math.max(0, quantity));
  }

  function updatePriceText(item, product, variantId) {
    const amount = item?.querySelector("[data-nept-price-amount]");
    if (!item || !amount) return;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((entry) => String(entry.id) === String(variantId)) || variants[0];
    const price = Number(variant?.price);
    if (Number.isFinite(price)) amount.textContent = formatMoney(price);
  }

  function initCursor() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    document.querySelectorAll(".nept-cursor").forEach((item, index) => {
      if (index > 0) item.remove();
    });

    let cursor = document.querySelector(".nept-cursor");
    if (!cursor) {
      cursor = document.createElement("div");
      cursor.className = "nept-cursor";
      cursor.setAttribute("aria-hidden", "true");
      document.body.appendChild(cursor);
    }
    document.body.classList.add("nept-cursor-enabled");

    const clickableSelector = 'a, button, [role="button"], input[type="submit"], [onclick]';
    const isClickable = (target) =>
      target instanceof Element &&
      (Boolean(target.closest(clickableSelector)) || window.getComputedStyle(target).cursor === "pointer");

    const updateClickableState = (target) => {
      cursor.classList.toggle("is-clickable", isClickable(target));
    };

    window.addEventListener("pointermove", (event) => {
      cursor.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0) translate(-50%, -50%)`;
      cursor.classList.add("is-active");
      updateClickableState(event.target);
    });

    document.addEventListener("pointerover", (event) => {
      updateClickableState(event.target);
    });

    document.addEventListener("pointerout", (event) => {
      updateClickableState(event.relatedTarget);
    });

    document.addEventListener("pointerleave", () => {
      cursor.classList.remove("is-active");
      cursor.classList.remove("is-clickable");
    });
  }

  function initReveal() {
    const items = document.querySelectorAll(".nept-reveal");
    if (!items.length) return;

    if (reducedMotion || !("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = entry.target.dataset.revealDelay || 0;
          entry.target.style.transitionDelay = `${delay}ms`;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.16 },
    );

    items.forEach((item) => observer.observe(item));
  }

  function initHeroWords() {
    document.querySelectorAll("[data-nept-hero-word]").forEach((word, index) => {
      if (reducedMotion) {
        word.style.opacity = "1";
        word.style.transform = "none";
        return;
      }

      word.animate(
        [
          { opacity: 0, transform: "translateY(1.2em)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        {
          delay: 140 + index * 95,
          duration: 820,
          easing: "cubic-bezier(.2,.85,.2,1)",
          fill: "forwards",
        },
      );
    });
  }

  function initCardTilt() {
    document.querySelectorAll(".nept-card-tilt").forEach((card) => {
      card.addEventListener("mousemove", (event) => {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        const rotateX = -(y / rect.height) * 6;
        const rotateY = (x / rect.width) * 6;
        card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
        card.style.transition = "transform 0.15s ease-out";
        card.style.boxShadow = "0 0 40px rgba(255,255,255,0.06), 0 0 80px rgba(255,255,255,0.03)";
      });
      card.addEventListener("mouseleave", () => {
        card.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
        card.style.transition = "transform 0.6s ease-out, box-shadow 0.6s ease-out";
        card.style.boxShadow = "none";
      });
    });
  }

  function initArchiveCarousel() {
    document.querySelectorAll("[data-nept-archive-carousel]").forEach((carousel) => {
      const viewport = carousel.querySelector("[data-nept-archive-viewport]");
      const slides = Array.from(carousel.querySelectorAll("[data-nept-archive-slide]"));
      const previous = carousel.querySelector("[data-nept-archive-prev]");
      const next = carousel.querySelector("[data-nept-archive-next]");
      if (!viewport || !slides.length) return;

      let activeIndex = 0;
      let startX = null;
      let hasDragged = false;
      let suppressClick = false;
      let activeTiltCleanup = null;

      const getOffset = (index) => {
        let offset = index - activeIndex;
        const half = slides.length / 2;
        if (offset > half) offset -= slides.length;
        if (offset < -half) offset += slides.length;
        return Math.max(-3, Math.min(3, offset));
      };

      const attachTiltToCenter = () => {
        if (activeTiltCleanup) {
          activeTiltCleanup();
          activeTiltCleanup = null;
        }

        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        const centerCard = carousel.querySelector(".nept-archive-card.is-active");
        if (!centerCard) return;

        const handleMove = (event) => {
          const rect = centerCard.getBoundingClientRect();
          const x = event.clientX - rect.left - rect.width / 2;
          const y = event.clientY - rect.top - rect.height / 2;
          const rotX = -(y / rect.height) * 6;
          const rotY = (x / rect.width) * 6;
          centerCard.style.transform = `translate(-50%, -50%) scale(1.05) perspective(800px) rotateX(${rotX}deg) rotateY(${rotY}deg)`;
          centerCard.style.transition = "transform 0.1s ease-out";
          centerCard.style.boxShadow = "0 0 40px rgba(255,255,255,0.06), 0 0 80px rgba(255,255,255,0.03)";
        };

        const handleLeave = () => {
          centerCard.style.transform = "translate(-50%, -50%) scale(1.05) perspective(800px) rotateX(0deg) rotateY(0deg)";
          centerCard.style.transition = "transform 0.6s ease-out, box-shadow 0.6s ease-out";
          centerCard.style.boxShadow = "none";
        };

        centerCard.addEventListener("mousemove", handleMove);
        centerCard.addEventListener("mouseleave", handleLeave);

        activeTiltCleanup = () => {
          centerCard.removeEventListener("mousemove", handleMove);
          centerCard.removeEventListener("mouseleave", handleLeave);
          centerCard.style.transform = "";
          centerCard.style.transition = "";
          centerCard.style.boxShadow = "none";
        };
      };

      const render = () => {
        slides.forEach((slide, index) => {
          const offset = getOffset(index);
          const hidden = Math.abs(offset) > 2;
          slide.dataset.neptArchivePosition = hidden ? "hidden" : String(offset);
          slide.classList.toggle("is-active", offset === 0);
        });
        attachTiltToCenter();
      };

      const setActive = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        render();
      };

      previous?.addEventListener("click", () => setActive(activeIndex - 1));
      next?.addEventListener("click", () => setActive(activeIndex + 1));

      carousel.addEventListener(
        "click",
        (event) => {
          const archiveLink = event.target.closest(".nept-archive-card__arrow");
          if (archiveLink) {
            event.preventDefault();
            event.stopPropagation();
            window.location.href = "/pages/nept-archive";
            return;
          }

          if (suppressClick) {
            event.preventDefault();
            event.stopPropagation();
            suppressClick = false;
            return;
          }

          const slide = event.target.closest("[data-nept-archive-slide]");
          if (!slide || slide.classList.contains("is-active")) return;

          event.preventDefault();
          setActive(Number(slide.dataset.neptArchiveIndex || 0));
        },
        true,
      );

      viewport.addEventListener("pointerdown", (event) => {
        startX = event.clientX;
        hasDragged = false;
        viewport.classList.add("is-dragging");
      });

      viewport.addEventListener(
        "pointermove",
        (event) => {
          if (startX === null) return;
          if (Math.abs(event.clientX - startX) > 8) hasDragged = true;
        },
        { passive: true },
      );

      viewport.addEventListener("pointerup", (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        viewport.classList.remove("is-dragging");
        if (!hasDragged || Math.abs(delta) <= 45) return;
        suppressClick = true;
        setActive(delta < 0 ? activeIndex + 1 : activeIndex - 1);
        window.setTimeout(() => {
          suppressClick = false;
        }, 180);
      });

      viewport.addEventListener("pointercancel", () => {
        startX = null;
        hasDragged = false;
        viewport.classList.remove("is-dragging");
      });

      render();
    });
  }

  function initDropImagePaint() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    document.querySelectorAll("[data-nept-drop-paint]").forEach((media) => {
      const image = media.querySelector(".nept-drop__image--mono");
      const canvas = media.querySelector("[data-nept-drop-paint-canvas]");
      const reset = media.closest(".nept-drop__visual")?.querySelector("[data-nept-drop-paint-reset]");
      if (!image || !canvas) return;

      const context = canvas.getContext("2d");
      const colorCanvas = document.createElement("canvas");
      const colorContext = colorCanvas.getContext("2d");
      let lastPoint = null;

      context.imageSmoothingEnabled = false;
      colorContext.imageSmoothingEnabled = false;

      const setupCanvas = (clearPaint = false) => {
        if (!image.complete || !image.naturalWidth) return;
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
          colorCanvas.width = width;
          colorCanvas.height = height;
          context.imageSmoothingEnabled = false;
          colorContext.imageSmoothingEnabled = false;
          lastPoint = null;
        }

        if (clearPaint) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          lastPoint = null;
        }

        colorContext.clearRect(0, 0, colorCanvas.width, colorCanvas.height);
        colorContext.drawImage(image, 0, 0);
      };

      const getPaintPoint = (event) => {
        const rect = media.getBoundingClientRect();
        const coverScale = Math.max(rect.width / canvas.width, rect.height / canvas.height);
        const renderedWidth = canvas.width * coverScale;
        const renderedHeight = canvas.height * coverScale;
        const offsetX = (rect.width - renderedWidth) / 2;
        const offsetY = (rect.height - renderedHeight) / 2;

        return {
          x: (event.clientX - rect.left - offsetX) / coverScale,
          y: (event.clientY - rect.top - offsetY) / coverScale,
          radius: 120 / coverScale,
        };
      };

      const stampBrush = (x, y, radius) => {
        const brushRadius = Math.max(1, Math.round(radius));
        const size = brushRadius * 2;
        const left = Math.round(x) - brushRadius;
        const top = Math.round(y) - brushRadius;
        const stampCanvas = document.createElement("canvas");
        const stampContext = stampCanvas.getContext("2d");
        stampCanvas.width = size;
        stampCanvas.height = size;
        stampContext.imageSmoothingEnabled = false;

        stampContext.drawImage(colorCanvas, -left, -top);

        const gradient = stampContext.createRadialGradient(brushRadius, brushRadius, brushRadius * 0.12, brushRadius, brushRadius, brushRadius);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(0.46, "rgba(255,255,255,1)");
        gradient.addColorStop(0.78, "rgba(255,255,255,0.42)");
        gradient.addColorStop(1, "rgba(255,255,255,0)");

        stampContext.globalCompositeOperation = "destination-in";
        stampContext.fillStyle = gradient;
        stampContext.fillRect(0, 0, size, size);
        context.drawImage(stampCanvas, left, top);
      };

      const paintTo = (event) => {
        if (!canvas.width || !canvas.height) setupCanvas();
        if (!canvas.width || !canvas.height) return;
        const point = getPaintPoint(event);
        const previous = lastPoint || point;
        const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
        const steps = Math.max(1, Math.ceil(distance / (point.radius * 0.18)));

        for (let step = 0; step <= steps; step += 1) {
          const progress = step / steps;
          stampBrush(
            previous.x + (point.x - previous.x) * progress,
            previous.y + (point.y - previous.y) * progress,
            point.radius,
          );
        }

        lastPoint = point;
      };

      const resetPaint = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        lastPoint = null;
      };

      if (image.complete) setupCanvas();
      image.addEventListener("load", () => setupCanvas(true));
      media.addEventListener("pointermove", paintTo);
      media.addEventListener("pointerleave", () => {
        lastPoint = null;
      });
      reset?.addEventListener("click", resetPaint);
    });
  }

  function initLiveInventory() {
    document.querySelectorAll("[data-nept-live-inventory]").forEach((item) => {
      const handle = item.dataset.neptProductHandle;
      const variantId = item.dataset.neptVariantId;
      const count = item.querySelector("[data-nept-inventory-count]");
      if (!handle || !count) return;

      loadProductJson(handle)
        .then((product) => updateInventoryText(item, product, variantId))
        .catch(() => {
          // Keep the Liquid-rendered fallback count if Shopify Ajax inventory is unavailable.
        });
    });

    document.querySelectorAll("[data-nept-live-price]").forEach((item) => {
      const handle = item.dataset.neptProductHandle;
      const variantId = item.dataset.neptVariantId;
      if (!handle) return;

      loadProductJson(handle)
        .then((product) => updatePriceText(item, product, variantId))
        .catch(() => {
          // Keep the Liquid-rendered fallback price if Shopify Ajax is unavailable.
        });
    });
  }

  function initProductPage() {
    document.querySelectorAll("[data-nept-product]").forEach((section) => {
      const handle = section.dataset.neptProductHandle;
      const primaryImage = section.querySelector("[data-nept-product-primary-image]");
      const gallery = section.querySelector("[data-nept-product-gallery]");
      const imagesData = section.querySelector("[data-nept-product-images]");
      const variantsData = section.querySelector("[data-nept-product-variants]");
      const prevButton = section.querySelector("[data-nept-product-gallery-prev]");
      const nextButton = section.querySelector("[data-nept-product-gallery-next]");
      const imageIndex = section.querySelector("[data-nept-product-image-index]");
      const imageTotal = section.querySelector("[data-nept-product-image-total]");
      const inventory = section.querySelector("[data-nept-live-inventory]");
      const variantInput = section.querySelector("[data-nept-product-variant-id]");
      const price = section.querySelector("[data-nept-product-price]");
      const addButton = section.querySelector("[data-nept-add-to-cart]");
      const addLabel = section.querySelector("[data-nept-add-to-cart-label]");
      const parseJson = (script, fallback) => {
        if (!script?.textContent) return fallback;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return fallback;
        }
      };
      const productImages = parseJson(imagesData, []);
      const variantMeta = new Map(parseJson(variantsData, []).map((variant) => [String(variant.id), variant]));
      let currentImageIndex = 0;
      let productData = null;

      const padNumber = (value) => String(value).padStart(2, "0");

      const renderImage = (index) => {
        if (!primaryImage || !productImages.length) return;
        currentImageIndex = (index + productImages.length) % productImages.length;
        const image = productImages[currentImageIndex];
        primaryImage.src = image.src || primaryImage.src;
        primaryImage.srcset = image.srcset || "";
        primaryImage.alt = image.alt || primaryImage.alt;
        if (gallery && image.ratio) gallery.style.setProperty("--nept-product-image-ratio", image.ratio);
        if (imageIndex) imageIndex.textContent = padNumber(currentImageIndex + 1);
        if (imageTotal) imageTotal.textContent = padNumber(productImages.length);
      };

      prevButton?.addEventListener("click", () => renderImage(currentImageIndex - 1));
      nextButton?.addEventListener("click", () => renderImage(currentImageIndex + 1));
      if (productImages.length) renderImage(0);

      const selectedOptions = () => {
        const options = [];
        section.querySelectorAll("[data-nept-product-option]:checked").forEach((option) => {
          options[Number(option.dataset.optionPosition) - 1] = option.value;
        });
        return options;
      };

      const findSelectedVariant = () => {
        const options = selectedOptions();
        const variants = Array.isArray(productData?.variants) ? productData.variants : [];
        return variants.find((variant) => variant.options.every((value, index) => options[index] === value));
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (addButton) addButton.disabled = true;
          if (addLabel) addLabel.textContent = "Unavailable";
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        if (inventory) {
          inventory.dataset.neptVariantId = variant.id;
          const inventoryCount = Number(variantMeta.get(String(variant.id))?.inventory);
          if (Number.isFinite(inventoryCount)) {
            const count = inventory.querySelector("[data-nept-inventory-count]");
            if (count) count.textContent = String(Math.max(0, inventoryCount));
          } else {
            updateInventoryText(inventory, productData, variant.id);
          }
        }
        if (price) price.textContent = formatMoney(variant.price);
        if (addButton) addButton.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? "Add to cart" : "Sold out";

        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      };

      section.querySelectorAll("[data-nept-product-option]").forEach((option) => {
        option.addEventListener("change", () => {
          if (productData) applyVariant(findSelectedVariant());
        });
      });

      loadProductJson(handle)
        .then((product) => {
          productData = product;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const current = variants.find((variant) => String(variant.id) === String(variantInput?.value));
          applyVariant(current || findSelectedVariant() || variants[0]);
        })
        .catch(() => {
          // Product form remains usable with the Liquid-rendered variant if Ajax is unavailable.
        });
    });
  }

  function initCartDrawer() {
    const drawer = document.querySelector("[data-nept-cart-drawer]");
    if (!drawer) return;

    const itemsContainer = drawer.querySelector("[data-nept-cart-items]");
    const emptyState = drawer.querySelector("[data-nept-cart-empty]");
    const footer = drawer.querySelector("[data-nept-cart-footer]");
    const subtotal = drawer.querySelector("[data-nept-cart-subtotal]");
    const panel = drawer.querySelector(".nept-cart-drawer__panel");
    const closeButtons = drawer.querySelectorAll("[data-nept-cart-close]");
    const cartChangeVersions = new Map();
    let animationTimer = null;

    const loadCart = () =>
      fetch("/cart.js", {
        headers: {
          Accept: "application/json",
        },
      }).then((response) => {
        if (!response.ok) throw new Error("Unable to load cart");
        return response.json();
      });

    const beginDrawerAnimation = () => {
      drawer.classList.add("is-animating");
      if (animationTimer) window.clearTimeout(animationTimer);
      animationTimer = window.setTimeout(() => drawer.classList.remove("is-animating"), 420);
    };

    const endDrawerAnimation = () => {
      drawer.classList.remove("is-animating");
      if (animationTimer) {
        window.clearTimeout(animationTimer);
        animationTimer = null;
      }
    };

    const openDrawer = () => {
      if (!drawer.classList.contains("is-open")) beginDrawerAnimation();
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("nept-cart-drawer-open");
      window.setTimeout(() => drawer.querySelector("[data-nept-cart-close]")?.focus(), 120);
    };

    const closeDrawer = () => {
      if (drawer.classList.contains("is-open")) beginDrawerAnimation();
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.classList.remove("nept-cart-drawer-open");
    };

    panel?.addEventListener("transitionend", (event) => {
      if (event.target === panel && event.propertyName === "transform") endDrawerAnimation();
    });

    const updateCartSummary = (cart) => {
      const items = Array.isArray(cart.items) ? cart.items : [];
      const hasItems = items.length > 0;

      if (subtotal) subtotal.textContent = formatMoney(cart.total_price || 0);
      if (emptyState) emptyState.hidden = hasItems;
      if (footer) footer.hidden = !hasItems;
    };

    const renderCart = (cart) => {
      const items = Array.isArray(cart.items) ? cart.items : [];

      if (itemsContainer) {
        itemsContainer.innerHTML = items
          .map((item) => {
            const title = escapeHtml(item.product_title || item.title);
            const variant = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
            const unitPrice = item.quantity > 0 ? Math.round(item.final_line_price / item.quantity) : 0;
            const image = item.image
              ? `<img src="${escapeHtml(item.image)}" alt="${title}" loading="lazy">`
              : `<span>${escapeHtml((item.product_title || item.title || "N").charAt(0))}</span>`;

            return `
              <article class="nept-cart-drawer__item" data-nept-cart-item data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity}" data-unit-price="${unitPrice}">
                <a class="nept-cart-drawer__image" href="${escapeHtml(item.url || "#")}" aria-label="View ${title}">
                  ${image}
                </a>
                <div class="nept-cart-drawer__item-main">
                  <div>
                    <a class="nept-cart-drawer__title" href="${escapeHtml(item.url || "#")}">${title}</a>
                    ${variant ? `<p class="nept-cart-drawer__variant">${escapeHtml(variant)}</p>` : ""}
                  </div>
                  <div class="nept-cart-drawer__quantity" aria-label="Quantity controls">
                    <button type="button" data-nept-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${Math.max(item.quantity - 1, 0)}" aria-label="Decrease quantity for ${title}">−</button>
                    <span data-nept-cart-quantity-value>${item.quantity}</span>
                    <button type="button" data-nept-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity for ${title}">+</button>
                  </div>
                  <button class="nept-cart-drawer__remove" type="button" data-nept-cart-remove data-key="${escapeHtml(item.key)}">
                    × Remove
                </button>
                </div>
                <p class="nept-cart-drawer__price" data-nept-cart-line-price>${formatMoney(item.final_line_price)}</p>
              </article>
            `;
          })
          .join("");
      }

      updateCartSummary(cart);
    };

    const findRenderedCartItem = (key) =>
      Array.from(itemsContainer?.querySelectorAll("[data-nept-cart-item]") || []).find((item) => item.dataset.key === key);

    const setRenderedCartItemQuantity = (item, quantity, linePrice) => {
      if (!item) return;
      const nextQuantity = Math.max(0, Number(quantity) || 0);
      const unitPrice = Number(item.dataset.unitPrice);
      const quantityValue = item.querySelector("[data-nept-cart-quantity-value]");
      const buttons = item.querySelectorAll("[data-nept-cart-change]");
      const price = item.querySelector("[data-nept-cart-line-price]");

      item.dataset.quantity = String(nextQuantity);
      if (quantityValue) quantityValue.textContent = String(nextQuantity);
      if (buttons[0]) buttons[0].dataset.quantity = String(Math.max(nextQuantity - 1, 0));
      if (buttons[1]) buttons[1].dataset.quantity = String(nextQuantity + 1);
      if (price) {
        const nextLinePrice = Number.isFinite(Number(linePrice))
          ? Number(linePrice)
          : Number.isFinite(unitPrice)
            ? unitPrice * nextQuantity
            : null;
        if (Number.isFinite(nextLinePrice)) price.textContent = formatMoney(nextLinePrice);
      }
    };

    const syncRenderedCartItem = (cart, key) => {
      const item = findRenderedCartItem(key);
      if (!item) return;

      const latest = (Array.isArray(cart.items) ? cart.items : []).find((entry) => entry.key === key);
      if (!latest) {
        item.remove();
        return;
      }

      if (latest.quantity > 0) {
        item.hidden = false;
        item.dataset.unitPrice = String(Math.round(latest.final_line_price / latest.quantity));
      }
      setRenderedCartItemQuantity(item, latest.quantity, latest.final_line_price);
    };

    const changeCartItem = (key, quantity) =>
      fetch("/cart/change.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: key, quantity }),
      }).then((response) => {
        if (!response.ok) throw new Error("Unable to update cart");
        return response.json();
      });

    closeButtons.forEach((button) => button.addEventListener("click", closeDrawer));

    document.querySelectorAll("[data-nept-cart-open]").forEach((button) => {
      button.addEventListener("click", () => {
        loadCart()
          .then(renderCart)
          .catch(() => {})
          .finally(openDrawer);
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) closeDrawer();
    });

    itemsContainer?.addEventListener("click", (event) => {
      const changeButton = event.target.closest("[data-nept-cart-change]");
      const removeButton = event.target.closest("[data-nept-cart-remove]");
      const target = changeButton || removeButton;
      if (!target) return;

      const key = target.dataset.key;
      const quantity = removeButton ? 0 : Number(target.dataset.quantity);
      if (!key || !Number.isFinite(quantity)) return;

      const item = target.closest("[data-nept-cart-item]");
      const version = (cartChangeVersions.get(key) || 0) + 1;
      cartChangeVersions.set(key, version);

      if (item) {
        if (quantity <= 0) {
          item.hidden = true;
        } else {
          setRenderedCartItemQuantity(item, quantity);
        }
      }

      changeCartItem(key, quantity)
        .then((cart) => {
          if (cartChangeVersions.get(key) !== version) return;
          syncRenderedCartItem(cart, key);
          updateCartSummary(cart);
        })
        .catch(() => loadCart().then(renderCart).catch(() => {}));
    });

    document.addEventListener("submit", (event) => {
      const form = event.target.closest(".nept-product__form");
      if (!form) return;

      event.preventDefault();
      const button = form.querySelector("[data-nept-add-to-cart]");
      const label = form.querySelector("[data-nept-add-to-cart-label]");
      const originalLabel = label?.textContent;

      if (button?.disabled) return;
      if (button) button.disabled = true;
      if (label) label.textContent = "Adding";

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: new FormData(form),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Unable to add item");
          return loadCart();
        })
        .then((cart) => {
          renderCart(cart);
          openDrawer();
        })
        .catch(() => {
          if (label) label.textContent = "Try again";
        })
        .finally(() => {
          if (button) button.disabled = false;
          if (label && label.textContent === "Adding") label.textContent = originalLabel || "Add to cart";
        });
    });

    loadCart().then(renderCart).catch(() => {});
  }

  function initNewsletterPopup() {
    const openers = document.querySelectorAll("[data-nept-newsletter-open]");
    const overlay = document.querySelector("[data-nept-newsletter]");
    if (!openers.length || !overlay) return;

    const close = overlay.querySelector("[data-nept-newsletter-close]");
    const form = overlay.querySelector(".nept-newsletter-form");
    const steps = overlay.querySelectorAll("[data-nept-newsletter-step]");
    const email = overlay.querySelector('input[type="email"]');
    const phone = overlay.querySelector("[data-nept-newsletter-phone-input]");
    const tagInput = overlay.querySelector("[data-nept-newsletter-tags]");
    const notifyEmail = overlay.querySelector("[data-nept-notify-email]");
    const notifySms = overlay.querySelector("[data-nept-notify-sms]");
    let wantsSms = false;

    const showStep = (step) => {
      steps.forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.neptNewsletterStep === step);
      });
    };

    const focusFirstField = (step) => {
      const active = overlay.querySelector(`[data-nept-newsletter-step="${step}"]`);
      const field = active?.querySelector("input:not([type='hidden']), button");
      window.setTimeout(() => field?.focus(), 60);
    };

    const validateEmail = () => {
      if (!email) return true;
      if (email.checkValidity()) return true;
      email.reportValidity();
      return false;
    };

    const setTags = () => {
      if (!tagInput) return;
      const tags = ["newsletter", "drop access"];
      tags.push(wantsSms ? "sms requested" : "email only");
      if (notifyEmail?.checked) tags.push("email alerts");
      if (wantsSms && notifySms?.checked) tags.push("sms alerts");
      tagInput.value = tags.join(", ");
    };

    const openPopup = () => {
      wantsSms = false;
      if (phone) {
        phone.required = false;
        phone.value = "";
      }
      if (notifyEmail) notifyEmail.checked = true;
      if (notifySms) notifySms.checked = true;
      showStep("1");
      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      window.setTimeout(() => email?.focus(), 60);
    };

    const closePopup = () => {
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
    };

    openers.forEach((button) => button.addEventListener("click", openPopup));
    close?.addEventListener("click", closePopup);
    overlay.querySelectorAll("[data-nept-newsletter-next]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!validateEmail()) return;
        const nextStep = button.dataset.neptNewsletterNext;
        showStep(nextStep);
        focusFirstField(nextStep);
      });
    });
    overlay.querySelectorAll("[data-nept-newsletter-phone]").forEach((button) => {
      button.addEventListener("click", () => {
        wantsSms = button.dataset.neptNewsletterPhone === "true";
        if (phone) phone.required = wantsSms;
        const nextStep = wantsSms ? "3a" : "3b";
        showStep(nextStep);
        focusFirstField(nextStep);
      });
    });
    if (form && steps.length) {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const activeStep = overlay.querySelector("[data-nept-newsletter-step].is-active")?.dataset.neptNewsletterStep;

        if (activeStep === "1") {
          if (!validateEmail()) return;
          showStep("2");
          focusFirstField("2");
          return;
        }

        if (!validateEmail()) {
          showStep("1");
          focusFirstField("1");
          return;
        }

        if (wantsSms && phone && !phone.checkValidity()) {
          phone.reportValidity();
          return;
        }

        setTags();
        const submitter = event.submitter;
        if (submitter) submitter.disabled = true;

        fetch(form.action, {
          method: "POST",
          body: new FormData(form),
          headers: { Accept: "text/html" },
        })
          .catch(() => {})
          .finally(() => {
            if (submitter) submitter.disabled = false;
            showStep("4");
            focusFirstField("4");
          });
      });
    }
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) closePopup();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePopup();
    });
  }

  function initScrollProgress() {
    const progress = document.querySelector("[data-nept-scroll-progress]");
    if (!progress) return;
    const clip = progress.querySelector(".nept-scroll-progress__clip");
    const value = progress.closest(".nept-scroll-progress")?.querySelector("[data-nept-scroll-progress-value]");

    let frame = null;

    const update = () => {
      frame = null;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percent = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 0;
      const clamped = Math.min(100, Math.max(0, percent));
      progress.style.setProperty("--nept-scroll-progress", `${clamped}%`);
      if (value) value.textContent = String(clamped);
      clip?.setAttribute("height", String((48 * clamped) / 100));
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  function initIdentityTextWipe() {
    const line = document.querySelector(".nept-story__body p:nth-child(2)");
    if (!line) return;

    line.setAttribute("data-nept-wipe-text", line.textContent.trim());

    let frame = null;

    const update = () => {
      frame = null;
      const rect = line.getBoundingClientRect();
      const start = window.innerHeight * 0.88;
      const end = window.innerHeight * 0.35;
      const raw = (start - rect.top) / (start - end);
      const progress = Math.min(1, Math.max(0, raw));
      line.style.setProperty("--nept-story-wipe", `${100 - progress * 100}%`);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  function initPhilosophyDividers() {
    const section = document.querySelector(".nept-philosophy");
    if (!section) return;

    if (reducedMotion) {
      section.style.setProperty("--nept-philosophy-divider-clip", "inset(0 0 0 0)");
      return;
    }

    let frame = null;

    const update = () => {
      frame = null;
      const rect = section.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));

      if (progress <= 0.5) {
        const bottomClip = 100 - progress * 200;
        section.style.setProperty("--nept-philosophy-divider-clip", `inset(0 0 ${bottomClip}% 0)`);
        return;
      }

      const topClip = (progress - 0.5) * 200;
      section.style.setProperty("--nept-philosophy-divider-clip", `inset(${topClip}% 0 0 0)`);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  document.addEventListener("DOMContentLoaded", () => {
    initCursor();
    initCartDrawer();
    initReveal();
    initHeroWords();
    initCardTilt();
    initArchiveCarousel();
    initDropImagePaint();
    initLiveInventory();
    initProductPage();
    initNewsletterPopup();
    initScrollProgress();
    initIdentityTextWipe();
    initPhilosophyDividers();
  });
})();
